<template>
  <FormBox
    slot="formBox"
    :headline="
      statusCode !== 500 && !emailConfirmed && !loading && !accountDeleted
        ? $t('auth.login.form.headline')
        : ''
    "
  >
    <template slot="form">
      <template v-if="statusCode === 500 || accountDeleted">
        <div v-if="statusCode === 500">
          <Headline size="s" responsive centered>
            {{ $t('auth.login.form.error.generic.headline') }}
          </Headline>

          <Copy size="xl" centered>{{ $t('auth.login.form.error.registration.subline') }}</Copy>
          <Copy size="m" color="tertiaryLight" centered></Copy>
        </div>

        <div v-if="accountDeleted" :class="`${el}__done`">
          <Icon color="supportDanger" name="lock-fill" :class="`${el}__doneIcon`" />

          <Headline size="s" responsive centered>
            {{ $t('auth.login.form.accountDeleted.headline') }}
          </Headline>

          <Copy size="xl" centered>{{ $t('auth.login.form.accountDeleted.subline') }}</Copy>
          <Copy size="m" color="tertiaryLight" centered>
            {{ $t('auth.login.form.accountDeleted.text') }}
          </Copy>
        </div>
      </template>

      <form v-else :class="classes()" @submit.prevent="handleSubmit">
        <div v-if="emailConfirmed" :class="`${el}__doiDone`">
          <Icon name="checkbox-circle-fill" color="supportSuccess" :class="`${el}__doneIcon`" />

          <Headline size="s" responsive centered>
            {{ $t('auth.login.form.done.headline') }}
          </Headline>

          <Copy size="xl" color="tertiaryLight" centered>
            {{ $t('auth.login.form.done.copy') }}
          </Copy>
        </div>
        <Alert
          v-if="logout"
          :class="`${el}__alert`"
          :headline="$t('auth.login.form.401.copy')"
          background="secondaryYellow"
        />
        <div v-if="loading" :class="`${el}__spinner`">
          <Spinner />
        </div>
        <template v-else>
          <div :class="`${el}__inputRow`">
            <BaseInput
              label="E-Mail"
              :type="$matchMedia.l ? 'text' : 'email'"
              name="email"
              v-model.trim="formFields.email.value"
              :error="formFields.email.error"
              @input="clearError(formFields.email)"
              clearable
            />
          </div>
          <div :class="`${el}__inputRow`">
            <div :class="`${el}__forgotPassword`" @click="trackClick('forgottenPassword')">
              <Link weight="regular" :to="ROUTE_FORGOT_PASSWORD" noUnderline>
                {{ $t('auth.login.form.forgotPassword') }}
              </Link>
            </div>
            <BaseInput
              label="Passwort"
              type="password"
              name="password"
              v-model="formFields.password.value"
              :error="formFields.password.error"
              @input="clearError(formFields.password)"
              clearable
              @iconClicked="trackClick('showPasswd')"
            />
            <Copy v-if="statusCode === 401" size="s" color="supportDanger" centered>
              {{ this.$t('auth.login.form.error.401') }}
            </Copy>
          </div>
          <div :class="`${el}__buttonRow`">
            <Button
              :label="$t('auth.login.form.button')"
              :loading="authStatus !== 'error' && authStatus !== ''"
              additionalIconName="arrow-right-line"
              submit
            />
          </div>
        </template>
      </form>
    </template>
  </FormBox>
</template>

<script>
import Vue from 'vue';
import BaseInput from '@/objects/Forms/BaseInput';
import Button from '@/objects/Button';
import Link from '@/objects/Link';
import Copy from '@/objects/Copy';
import Icon from '@/objects/Icon';
import Alert from '@/objects/Alert';
import Spinner from '@/objects/Spinner';
import Headline from '@/objects/Headline';
import FormBox from '@/components/FormBox';
import { emailPattern } from '@/helpers/patterns';
import { validateForm } from '@/helpers/formValidation';
import {
  ROUTE_LOGIN,
  ROUTE_INTRO,
  ROUTE_DASHBOARD,
  ROUTE_FORGOT_PASSWORD,
  ROUTE_PRIVACY,
} from '@/constants/routes';
import { HEALTH_REQUEST } from '@/store/actions/health';

import { AUTH_REQUEST } from '@/store/actions/auth';
import { mapFieldsAuth } from '@/store/mapFields';

import {
  ROUTE_API_CONFIRM_EMAIL,
  ROUTE_API_REGISTER_USER,
  ROUTE_API_REGISTER_TOKEN,
} from '@/constants/apiRoutes';

import { LS_CARE_LEVEL_CALCULATOR_JWT } from '@/constants/storage';

import { createTrackingData } from '@/helpers/tracking/utils/helpers';
import { getBiTrackingData } from '@/helpers/biTracking/biTracking';
import { createValidationData } from '@/helpers/tracking/validationTracking';

export default {
  name: 'LoginForm',
  components: {
    BaseInput,
    Button,
    Link,
    Copy,
    Icon,
    Alert,
    Spinner,
    Headline,
    FormBox,
  },
  props: {},
  data() {
    const { token } = this.$route.params;

    return {
      ROUTE_PRIVACY,
      ROUTE_FORGOT_PASSWORD,
      el: 'c-loginForm',
      statusCode: null,
      logout: false,
      accountDeleted: false,
      token,
      emailConfirmed: false,
      loading: false,
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
      formFields: {
        email: {
          value: '',
          error: null,
          required: this.$t('auth.register.form.error.email.required'),
          pattern: {
            value: emailPattern,
            message: this.$t('auth.register.form.error.email.formatted'),
          },
        },
        password: {
          value: '',
          error: null,
          required: this.$t('auth.register.form.error.password.required'),
        },
      },
    };
  },
  created() {
    if (this.token) {
      this.confirmEmail();
    }

    this.setStatesFromUrl();
  },
  computed: {
    ...mapFieldsAuth(['isAuthenticated', 'authStatus']),
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler() {
        this.setStatesFromUrl();
      },
    },

    '$store.state.auth.isAuthenticated': function () {
      if (this.isAuthenticated) {
        /**
         * Nach erfolgreichem Login werden die Gesundheitsdaten geholt.
         * Wenn es noch keine gibt, dann wird ins Intro weiterleitet.
         * Anderenfalls ins Dashboard.
         */
        this.$store.dispatch(HEALTH_REQUEST).then((res) => {
          const { showIntro } = res;
          if (showIntro) {
            this.$router.push(ROUTE_INTRO);
          } else {
            this.$router.push(ROUTE_DASHBOARD);
          }
        });
      }
    },
  },
  methods: {
    setStatesFromUrl() {
      if (this.$route.query.logout) {
        this.logout = !!this.$route.query.logout;
      }

      if (this.$route.query.statusCode) {
        this.statusCode = parseInt(this.$route.query.statusCode);
      }

      if (this.$route.query.account === 'deleted') {
        this.accountDeleted = true;
      }
    },

    clearError(field) {
      field.error = null;
    },

    handleSubmit(e) {
      this.statusCode = null;
      const { isValid } = validateForm(e.target.elements, this.formFields);
      if (isValid) {
        this.login();
      } else {
        createValidationData(this.formFields);
      }
    },

    login() {
      this.$store
        .dispatch(AUTH_REQUEST, {
          username: this.formFields.email.value,
          password: this.formFields.password.value,
        })
        .then((response) => {
          this.statusCode = response.status;
          if (this.statusCode === 200) {
            this.trackClick('loginBtn');
          }
        })
        .catch((e) => {
          // Error-Codes
          // 401: wrong credentials
          const { status } = e.response;
          this.statusCode = status;
        });
    },

    catchError() {
      this.$router.push(ROUTE_LOGIN);
      this.loading = false;
    },

    confirmEmail() {
      this.loading = true;

      const formData = new FormData();
      formData.append(`account[token]`, this.token);

      const data = {};
      data.tracking = getBiTrackingData();
      const jwt = localStorage.getItem(LS_CARE_LEVEL_CALCULATOR_JWT);
      if (jwt) {
        data.jwt = jwt;
      }

      Vue.axios
        .post(ROUTE_API_CONFIRM_EMAIL, formData)
        .then((response) => {
          if (response.status === 200) {
            // register user in health data backend
            Vue.axios
              .post(ROUTE_API_REGISTER_USER, data)
              .then((responseRegisterUser) => {
                if (responseRegisterUser.status === 200) {
                  Vue.axios
                    .post(ROUTE_API_REGISTER_TOKEN, formData)
                    .then((responseRegisterToken) => {
                      if (responseRegisterToken.status === 200) {
                        this.emailConfirmed = true;
                        this.loading = false;
                        localStorage.removeItem(LS_CARE_LEVEL_CALCULATOR_JWT);
                      }
                    })
                    .catch(this.catchError);
                }
              })
              .catch(() => {
                this.statusCode = 500;
                this.loading = false;
              });
          }
        })
        .catch(this.catchError);
    },

    trackClick(clickedElement) {
      let labelText = '';
      let eventText = '';
      let product = 'Anmelden';

      switch (clickedElement) {
        case 'forgottenPassword':
          eventText = 'link';
          labelText = product + '_link-' + this.$t('auth.login.form.forgotPassword');
          break;
        case 'loginBtn':
          eventText = 'button';
          labelText = product + '_btn-' + this.$t('auth.login.form.button');
          break;
        case 'showPasswd':
          eventText = 'element';
          labelText = product + '_click-' + 'Passwort Anzeigen';
          break;
      }
      createTrackingData(product, labelText, eventText);
    },
  },
};
</script>
